import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  JobStatusBadge,
  JobWaypointTracker,
  JobCoverageMap,
  JobLivePanel,
} from '../../components/jobs';
import { ClipboardList, Play, Pause, RotateCcw, CheckCircle, Wrench, Link } from 'lucide-react';
import { pingIfWanted } from '../../services/presencePinger';
import jobService, { JOB_OPERATE_SEATS } from '../../services/jobs';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import trackingController from '../../services/trackingController';
import waypointQueue from '../../services/waypointQueue';
import { uploadBeforeFinish, describeUploadFailure, stopTrackingFor } from '../../services/finishTracking';
import type { Job, LocationRecord, CoverageResult } from '../../types/jobs';

function JobOperatorView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Anyone may run a job they are on; the job pages beyond this one need a seat.
  // Without one, finishing goes back to the machine rather than to a 403.
  const { hasAccess: canOpenJobs } = useModuleLicense('farm_jobs', JOB_OPERATE_SEATS);
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [waypoints, setWaypoints] = useState<LocationRecord[]>([]);
  const [coverage, setCoverage] = useState<CoverageResult | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  // GPS points still on the phone. Once the job is complete the server refuses
  // them, so they are uploaded first and only left behind when asked to.
  const [pointsLeft, setPointsLeft] = useState<number | null>(null);
  const [strandedPoints, setStrandedPoints] = useState(0);

  // Hours-end field (hours-start is captured in the equipment pre-start flow)
  const [hoursEnd, setHoursEnd] = useState('');

  const jobId = parseInt(id || '0', 10);
  const myAssignment = job?.assignments.find(a => a.user_id === user?.id);
  const hasEquipment = job && job.equipment.length > 0;
  const primaryEquip = job?.equipment.find(e => e.role === 'primary');
  const implements_ = job?.equipment.filter(e => e.role === 'implement') || [];
  const isTracking = myAssignment?.status === 'in_progress';
  const leaveTo = canOpenJobs ? `/jobs/${jobId}`
    : primaryEquip ? `/equipment/${primaryEquip.equipment_id}` : '/';

  const loadJob = useCallback(async () => {
    try {
      const res = await jobService.getJob(jobId);
      if (res.success) setJob(res.data);
    } catch (e) {
      console.error('Load job error', e);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const loadMapData = async () => {
    try {
      const [wpRes, covRes] = await Promise.all([
        jobService.getWaypoints(jobId),
        job?.boundary_geometry && job?.working_width_m ? jobService.getCoverage(jobId) : Promise.resolve({ success: true, data: null as any }),
      ]);
      if (wpRes.success) setWaypoints(wpRes.data);
      if (covRes.success && covRes.data) setCoverage(covRes.data);
    } catch (e) {
      console.error('Map data error', e);
    }
  };

  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => { if (job) loadMapData(); }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch the job whenever the upload queue reports a 409 for this job, or
  // when the page becomes visible again. The queue's 409 means our cached
  // assignment/job statuses don't agree with the DB — without refetching, the
  // contextual Pause/Resume button keeps offering the wrong action.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void loadJob();
    };
    document.addEventListener('visibilitychange', onVisible);
    const unsub = waypointQueue.subscribeStatus((s) => {
      const e = s.lastError;
      if (e?.status === 409 && e.entityType === 'job' && e.entityId === jobId) {
        void loadJob();
      }
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      unsub();
    };
  }, [jobId, loadJob]);

  // Refresh map data periodically when in progress
  useEffect(() => {
    if (!isTracking) return;
    const interval = setInterval(loadMapData, 30_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTracking]);

  const handleStart = async () => {
    // If the job has equipment, go through the pre-start flow for checklist +
    // meter readings; backend startJob is triggered from there.
    if (hasEquipment && primaryEquip) {
      navigate(`/equipment/${primaryEquip.equipment_id}/pre-start?job_id=${jobId}`);
      return;
    }

    setActionLoading(true);
    try {
      const res = await jobService.startJob(jobId);
      if (res.success) {
        setJob(res.data);
        void pingIfWanted(true);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    setActionLoading(true);
    try {
      // Stop capturing NEW waypoints, but keep the queue auto-sync running so
      // any points still in IndexedDB keep uploading in the background.
      await trackingController.pauseCapture();
      const res = await jobService.pauseJob(jobId);
      if (res.success) setJob(res.data);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      const res = await jobService.resumeJob(jobId);
      if (res.success) {
        setJob(res.data);
        void pingIfWanted(true);
      }
      // Server state just flipped to in_progress — drain any waypoints that
      // had been stuck behind 409s. Without this, the queue can sit on its
      // adaptive backoff (up to 5 min) before retrying, leaving the "Job
      // paused on the server" banner up despite a successful resume.
      void waypointQueue.flush({ drain: true });
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (discardPoints = false) => {
    setCompleteError(null);
    setActionLoading(true);
    const entity = { type: 'job' as const, id: jobId };
    let restore: () => Promise<void> = async () => {};
    try {
      if (!discardPoints) {
        const upload = await uploadBeforeFinish(entity, setPointsLeft);
        setPointsLeft(null);
        restore = upload.restore;
        if (upload.remaining > 0) {
          await restore();
          setStrandedPoints(upload.remaining);
          setCompleteError(describeUploadFailure(upload));
          return;
        }
      }
      setStrandedPoints(0);
      const res = await jobService.completeJob(jobId, {
        hours_end: hoursEnd ? parseFloat(hoursEnd) : undefined,
      });
      if (res.success) {
        await stopTrackingFor(entity);
        setJob(res.data);
        navigate(leaveTo);
      } else {
        await restore();
        setCompleteError('Failed to complete job. Please try again.');
      }
    } catch (e: any) {
      await restore();
      setCompleteError(e?.message || 'Failed to complete job');
    } finally {
      setPointsLeft(null);
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-center"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" /></div>;
  }

  if (!job || !myAssignment) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-neutral-500">{!job ? 'Job not found' : 'You are not assigned to this job'}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate(canOpenJobs ? '/jobs' : '/equipment')}>
          {canOpenJobs ? 'Back to Jobs' : 'Back to Equipment'}
        </Button>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={job.name}
        subtitle="Operator View"
        icon={<ClipboardList className="h-6 w-6 text-white" />}
        actionButtons={<JobStatusBadge status={job.status} />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Equipment info */}
        {hasEquipment && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-4">
            <h3 className="text-sm font-medium text-neutral-700 mb-2 flex items-center gap-1.5">
              <Wrench className="h-4 w-4" /> Equipment
            </h3>
            {primaryEquip && (
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-neutral-800 font-medium">{primaryEquip.name}</span>
                <span className="text-neutral-500">{primaryEquip.make} {primaryEquip.model}</span>
              </div>
            )}
            {implements_.length > 0 && (
              <div className="text-sm text-neutral-600 flex items-center gap-1.5">
                <Link className="h-3.5 w-3.5" />
                {implements_.map(i => i.name).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Pre-start: for non-equipment jobs, start directly.
            For equipment jobs, hand off to the equipment pre-start flow
            (meters + checklist) which calls startJob when done. */}
        {myAssignment.status === 'assigned' && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-4 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-800">Pre-Start</h3>

            {hasEquipment ? (
              <p className="text-sm text-neutral-600">
                This job uses {primaryEquip?.name ?? 'equipment'}. Run the pre-start checklist to record meter readings and safety checks.
              </p>
            ) : null}

            <Button variant="primary" size="sm" onClick={handleStart} disabled={actionLoading} className="w-full">
              <Play className="h-4 w-4" />
              {hasEquipment ? 'Run Pre-Start' : actionLoading ? 'Starting...' : 'Start Job'}
            </Button>
          </div>
        )}

        {/* Active: GPS tracker */}
        {(myAssignment.status === 'in_progress' || myAssignment.status === 'paused') && (
          <>
            {/* Live stats — coverage, elapsed time, ETA */}
            <div className="mb-4">
              <JobLivePanel jobId={jobId} isActive={isTracking} />
            </div>

            {/* GPS Tracker */}
            {job.enable_gps_tracking && (
              <div className="mb-4">
                <JobWaypointTracker
                  jobId={jobId}
                  isActive={myAssignment.status === 'in_progress'}
                  hints={{ workingWidthM: job.working_width_m, targetAreaHa: job.target_area_hectares }}
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 mb-4">
              {myAssignment.status === 'in_progress' && (
                <Button variant="outline" size="sm" onClick={handlePause} disabled={actionLoading} className="flex-1">
                  <Pause className="h-4 w-4" /> Pause
                </Button>
              )}
              {myAssignment.status === 'paused' && (
                <Button variant="primary" size="sm" onClick={handleResume} disabled={actionLoading} className="flex-1">
                  <RotateCcw className="h-4 w-4" /> Resume
                </Button>
              )}
            </div>

            {/* Completion section */}
            <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-4 space-y-3">
              <h3 className="text-sm font-semibold text-neutral-800">Complete Job</h3>
              {hasEquipment && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Ending Hours</label>
                  <input
                    type="number"
                    step="0.1"
                    value={hoursEnd}
                    onChange={e => setHoursEnd(e.target.value)}
                    placeholder="Enter ending hours"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
              )}
              {completeError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{completeError}</p>
              )}
              {pointsLeft != null && pointsLeft > 0 && (
                <p className="text-xs text-neutral-500">
                  Uploading GPS track — {pointsLeft} point{pointsLeft === 1 ? '' : 's'} to go…
                </p>
              )}
              <Button
                size="sm"
                onClick={() => handleComplete()}
                disabled={actionLoading}
                className="w-full bg-success-600 hover:bg-success-700 text-white"
              >
                <CheckCircle className="h-4 w-4" />
                {actionLoading
                  ? (pointsLeft ? 'Uploading GPS…' : 'Completing...')
                  : strandedPoints > 0 ? 'Try again' : 'Complete Job'}
              </Button>
              {strandedPoints > 0 && !actionLoading && (
                <Button variant="outline" size="sm" onClick={() => handleComplete(true)} className="w-full">
                  Complete without them
                </Button>
              )}
            </div>

            {/* Mini coverage map */}
            {(job.boundary_geometry || job.targets?.length || waypoints.length > 0) && (
              <JobCoverageMap
                boundaryGeometry={job.boundary_geometry}
                targets={job.targets}
                waypoints={waypoints}
                coverage={coverage}
                height="300px"
              />
            )}
          </>
        )}

        {/* Completed state */}
        {myAssignment.status === 'completed' && (
          <div className="bg-success-50 border border-success-200 rounded-lg p-4 text-center">
            <CheckCircle className="h-8 w-8 text-success-600 mx-auto mb-2" />
            <p className="font-medium text-success-800">Your assignment is complete</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(leaveTo)}>
              {canOpenJobs ? 'View Job Details' : 'Back to the machine'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default JobOperatorView;
